import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Camera } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import useSafeBack from "@/hooks/useSafeBack";
import DocumentTitle from "@/components/DocumentTitle";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });
const YELLOW = "#FFD60A";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";
const META = "var(--ww-text-muted)";

/**
 * Msg 234 fix 4 · Stories-row lite. Tapping "your story" from For You lands
 * here. The list shows the user's verified-went events (self-declared or
 * organiser scanned) and links each one straight into the photo composer on
 * that event page via ?compose=photo.
 */
export default function NewStoryPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const onBack = useSafeBack("/app");

  useEffect(() => {
    if (!user) { setRows([]); return; }
    const t = localStorage.getItem("session_token");
    client.get("/me/attended", {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      withCredentials: true,
    })
      .then((r) => setRows(r.data.attended || []))
      .catch(() => setRows([]));
  }, [user]);

  return (
    <div className="flex h-full flex-col" data-testid="new-story-page"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title="new story · watu wako" />
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-10" style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 720 }}>
          <button type="button" onClick={onBack} data-testid="new-story-back" aria-label="back"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, color: META,
                           fontFamily: "Arial", fontSize: 13, padding: "10px 4px", minHeight: 44,
                           background: "transparent", border: 0, cursor: "pointer" }}>
            <ArrowLeft size={16} /> back
          </button>
          <header className="mt-2 mb-5">
            <h1 className="lowercase"
                style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                         fontSize: 32, lineHeight: 1.02 }}>
              your story
            </h1>
            <p style={{ fontFamily: "Arial", fontSize: 14, color: META, marginTop: 6 }}>
              pick an event you went to. we'll open the photo composer on that page.
            </p>
          </header>

          {!user && (
            <div className="rounded-2xl border p-8 text-center"
                 style={{ borderColor: LINE, background: CARD }}
                 data-testid="new-story-signed-out">
              <p style={{ fontFamily: "Arial", color: META, fontSize: 14 }}>
                sign in to share a story.
              </p>
              <Link to="/app/signin" data-testid="new-story-signin"
                    style={{ display: "inline-block", marginTop: 12, padding: "10px 20px", borderRadius: 999,
                             background: YELLOW, color: "#1A1A30",
                             fontFamily: "Poppins", fontWeight: 600, textDecoration: "none", textTransform: "lowercase" }}>
                sign in
              </Link>
            </div>
          )}

          {user && rows === null && (
            <p style={{ fontFamily: "Arial", color: META, padding: "24px 0" }}>loading…</p>
          )}

          {user && rows && rows.length === 0 && (
            <div className="rounded-2xl border p-8 text-center"
                 style={{ borderColor: LINE, background: CARD }}
                 data-testid="new-story-empty">
              <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16, textTransform: "lowercase", color: "var(--ww-text)" }}>
                no went events yet
              </p>
              <p style={{ fontFamily: "Arial", fontSize: 13, color: META, marginTop: 6, lineHeight: 1.5 }}>
                mark <span style={{ color: "var(--ww-text)" }}>i went</span> on an event once it starts, then come back to share a photo.
              </p>
              <Link to="/app/discover" data-testid="new-story-browse"
                    style={{ display: "inline-block", marginTop: 12, padding: "10px 20px", borderRadius: 999,
                             background: YELLOW, color: "#1A1A30",
                             fontFamily: "Poppins", fontWeight: 700, textDecoration: "none", textTransform: "lowercase" }}>
                browse events
              </Link>
            </div>
          )}

          {user && rows && rows.length > 0 && (
            <div className="flex flex-col gap-3" data-testid="new-story-list">
              {rows.map((ev) => (
                <Link key={ev.id} to={`/app/event/${ev.id}?compose=photo`}
                      data-testid={`new-story-row-${ev.id}`}
                      style={{ display: "flex", alignItems: "center", gap: 12,
                               padding: 12, borderRadius: 12,
                               background: CARD, border: `1px solid ${LINE}`,
                               textDecoration: "none", color: "inherit" }}>
                  {ev.cover_image_url ? (
                    <img src={ev.cover_image_url} alt=""
                         style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 56, height: 56, borderRadius: 10, background: "var(--ww-sunken)",
                                  display: "grid", placeItems: "center", flexShrink: 0, color: META }}>
                      <Camera size={20} />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="lowercase"
                       style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 15,
                                letterSpacing: "-0.02em", color: "var(--ww-text)",
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {(ev.title || "").toLowerCase()}
                    </p>
                    <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 2,
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {(ev.start_at || "").slice(0, 10)}
                      {ev.venue_display ? ` · ${ev.venue_display.toLowerCase()}` : ""}
                    </p>
                  </div>
                  <span style={{ padding: "8px 12px", borderRadius: 999,
                                 background: YELLOW, color: "#1A1A30",
                                 fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
                                 textTransform: "lowercase", flexShrink: 0,
                                 display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Camera size={12} /> share
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
